import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import WebApp from '@twa-dev/sdk';
import { getTelegramInitData } from '../utils/initData';
import { useCartStore } from '../store/cart.store';
import { useCheckoutStore, CheckoutFormData, DeliveryAddress } from '../store/checkout.store';
import { useProfileStore } from '../store/profile.store';
import { createOrder, getOrderStatus, uploadReceipt, cancelOrder } from '../api/orders.api';
import { calculateBonusForCart } from '../api/bonus.api';
import { validatePromocode as apiValidatePromocode } from '../api/promocode.api';
import { getMe, UserMeResponse } from '../api/users.api';
import { useCustomerConfig } from '../hooks/useCustomerConfig';
import { BottomNavigation } from '../components/BottomNavigation';
import { AppFooter } from '../components/AppFooter';
import { calculateDelivery, type DeliveryCalculateResponse } from '../api/delivery.api';
import {
  deliveryCopy,
  deliveryConditionsWorkingHours,
  deliveryConditionsAfterHours,
  deliveryConditionsAvgTime,
  deliveryConditionsZonesFallback,
  validationDateRequired,
  validationTimeRequired,
  loadingSlots,
  selectDateFirst,
  noSlotsPlaceholder,
} from '../copy/delivery';

type ApiErrorShape = {
  error?: {
    message?: unknown;
  };
};

const normalizeForMatch = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^г\.\s*/u, '')
    .replace(/^город\s+/u, '')
    .replace(/\s+/g, ' ');

const resolveDeliveryZoneCity = (
  rawCity: string | undefined | null,
  zones: Array<{ name: string }> | undefined,
  fallbackCity: string
): string => {
  const incoming = String(rawCity ?? '').trim();
  const normalizedIncoming = normalizeForMatch(incoming);
  if (!normalizedIncoming) return fallbackCity;

  const zoneNames = zones?.map((zone) => String(zone.name).trim()).filter(Boolean) ?? [];
  if (zoneNames.length === 0) return incoming || fallbackCity;

  const exact = zoneNames.find((name) => normalizeForMatch(name) === normalizedIncoming);
  if (exact) return exact;

  const partial = zoneNames.find((name) => {
    const normalizedZone = normalizeForMatch(name);
    return normalizedIncoming.includes(normalizedZone) || normalizedZone.includes(normalizedIncoming);
  });
  if (partial) return partial;

  return incoming || fallbackCity;
};

const extractApiErrorMessage = (error: unknown): string | null => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as unknown;
    if (data && typeof data === 'object') {
      const message = (data as ApiErrorShape)?.error?.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return null;
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCartStore();
  const {
    formData: savedFormData,
    saveFormData,
    pendingOrderId: storedOrderId,
    pendingOrderNumber: storedOrderNumber,
    pendingOrderTotal: storedOrderTotal,
    pendingOrderCreatedAt: storedOrderCreatedAt,
    paymentStep: storedPaymentStep,
    setPendingOrder,
    clearPendingOrder,
  } = useCheckoutStore();
  const { addresses: savedAddresses } = useProfileStore();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [orderTotal, setOrderTotal] = useState<number | null>(null);
  const [me, setMe] = useState<UserMeResponse | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'confirmed' | 'rejected'>('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [paymentStep, setPaymentStep] = useState<'form' | 'payment'>('form');
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [receiptFileName, setReceiptFileName] = useState<string | null>(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [orderBonusUsed, setOrderBonusUsed] = useState<number>(0);
  const [bonusEligibility, setBonusEligibility] = useState<{ maxToUse: number; hasPromoItems: boolean } | null>(null);
  const [cancelOrderLoading, setCancelOrderLoading] = useState(false);
  const [promocodeInput, setPromocodeInput] = useState(() => savedFormData?.promocode ?? '');
  const [promocodeApplying, setPromocodeApplying] = useState(false);
  const [promocodeApplied, setPromocodeApplied] = useState<{
    success: boolean;
    discountAmount: number;
    finalPrice: number;
    reason?: string;
    code?: string;
  } | null>(null);
  const { config: customerConfig } = useCustomerConfig();
  const [deliveryContext, setDeliveryContext] = useState<DeliveryCalculateResponse | null>(null);
  const [deliveryContextLoading, setDeliveryContextLoading] = useState(false);
  const promocodesEnabled = customerConfig?.promocodesEnabled !== false;

  // Загружаем сохраненные данные или используем значения по умолчанию
  const getInitialFormData = (): CheckoutFormData => {
    if (savedFormData) {
      return savedFormData;
    }
    
    // Получаем имя из Telegram, если доступно
    const userName = WebApp.initDataUnsafe?.user?.first_name || '';
    
    return {
      name: userName,
      phone: '',
      email: '',
      deliveryType: 'delivery',
      address: {
        city: 'Чебоксары',
        street: '',
        house: '',
        apartment: '',
      },
      deliveryAsReady: false,
      deliveryTimeType: 'approximate',
      deliveryDate: '',
      deliveryTime: '',
      recipientName: '',
      recipientPhone: '',
      cardText: '',
      comment: '',
      paymentType: 'yandex_pay',
      useBonuses: false,
      promocode: '',
    };
  };

  const [formData, setFormData] = useState<CheckoutFormData>(() => getInitialFormData());

  /** Fetch delivery context from backend (single source of truth). */
  useEffect(() => {
    let cancelled = false;
    setDeliveryContextLoading(true);
    const mode = formData.deliveryType === 'pickup' ? 'pickup' : 'delivery';
    calculateDelivery({
      mode,
      city: mode === 'delivery' ? (formData.address?.city ?? null) : null,
      deliveryDate: formData.deliveryDate ?? null,
      deliveryTime: formData.deliveryTime ?? null,
      deliveryAsReady: false,
      deliveryTimeType: mode === 'delivery' ? (formData.deliveryTimeType ?? 'approximate') : null,
    })
      .then((data) => {
        if (!cancelled) setDeliveryContext(data);
      })
      .catch(() => {
        if (!cancelled) setDeliveryContext(null);
      })
      .finally(() => {
        if (!cancelled) setDeliveryContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formData.deliveryType,
    formData.address?.city,
    formData.deliveryDate,
    formData.deliveryTime,
    formData.deliveryAsReady,
    formData.deliveryTimeType,
  ]);

  /** Backend-driven: earliest date/time and allowed slots. Fallback when context not yet loaded. */
  const earliestDelivery = deliveryContext
    ? {
        afterHours: deliveryContext.isAfterHours,
        earliestDate: deliveryContext.earliestDeliveryDate,
        earliestTime: deliveryContext.earliestDeliveryTime,
      }
    : { afterHours: false, earliestDate: '', earliestTime: '09:00' };

  /** Min time for selected date from backend allowedTimeSlots (no local Date logic). */
  const getMinDeliveryTime = useCallback(
    (dateValue?: string): string => {
      if (!dateValue || !deliveryContext) return '';
      const day = deliveryContext.allowedTimeSlots.find((d) => d.date === dateValue);
      return day?.slots?.[0] ?? '';
    },
    [deliveryContext]
  );

  const validateForm = useCallback((data: CheckoutFormData = formData): boolean => {
    const newErrors: Record<string, string> = {};

    if (!data.name.trim()) {
      newErrors.name = 'Введите имя';
    }

    if (!data.phone.trim()) {
      newErrors.phone = 'Введите телефон';
    } else if (!/^(\+7|8)?[\s-]?\(?[0-9]{3}\)?[\s-]?[0-9]{3}[\s-]?[0-9]{2}[\s-]?[0-9]{2}$/.test(data.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Введите корректный номер телефона';
    }

    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = 'Введите корректный email';
    }

    if (data.deliveryType === 'delivery') {
      const cityTrimmed = data.address.city != null ? String(data.address.city).trim() : '';
      if (!cityTrimmed) {
        newErrors.city = 'Укажите район или город доставки';
      }
      if (!data.address.street.trim()) {
        newErrors.street = 'Введите улицу';
      }
      if (!data.address.house.trim()) {
        newErrors.house = 'Введите номер дома';
      }
    }

    if (data.deliveryType === 'delivery' || data.deliveryType === 'pickup') {
      if (!data.deliveryDate) {
        newErrors.deliveryDate = data.deliveryType === 'pickup' ? 'Выберите дату самовывоза' : validationDateRequired;
      } else if (deliveryContext) {
        // Delivery time validation is backend-only.
        // Frontend only allows selecting values returned in allowedTimeSlots.
        const allowedDates = deliveryContext.allowedTimeSlots.map((d) => d.date);
        if (allowedDates.length && !allowedDates.includes(data.deliveryDate)) {
          newErrors.deliveryDate = selectDateFirst;
        }
      }
      if (!data.deliveryTime) {
        newErrors.deliveryTime = data.deliveryType === 'pickup' ? 'Выберите примерное время самовывоза' : validationTimeRequired;
      } else if (data.deliveryDate && deliveryContext) {
        // Delivery time validation is backend-only.
        const daySlots = deliveryContext.allowedTimeSlots.find((d) => d.date === data.deliveryDate);
        const slots = daySlots?.slots ?? [];
        if (slots.length && !slots.includes(data.deliveryTime)) {
          newErrors.deliveryTime =
            data.deliveryType === 'pickup'
              ? 'Выберите доступное время самовывоза'
              : data.deliveryTimeType === 'approximate'
                ? 'Выберите доступное окно доставки'
                : 'Выберите доступное время доставки';
        }
      }
    }

    if (!data.recipientName?.trim()) {
      newErrors.recipientName = 'Введите имя получателя';
    }

    if (!data.recipientPhone?.trim()) {
      newErrors.recipientPhone = 'Введите телефон получателя';
    } else if (!/^(\+7|8)?[\s-]?\(?[0-9]{3}\)?[\s-]?[0-9]{3}[\s-]?[0-9]{2}[\s-]?[0-9]{2}$/.test(data.recipientPhone.replace(/\s/g, ''))) {
      newErrors.recipientPhone = 'Введите корректный номер';
    }

    // cardText (открытка) is optional.

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, getMinDeliveryTime, earliestDelivery.afterHours, earliestDelivery.earliestDate, earliestDelivery.earliestTime, deliveryContext]);

  const showAlert = useCallback((message: string) => {
    try {
      if (WebApp?.showAlert) {
        WebApp.showAlert(message);
        return;
      }
    } catch {
      // fallback below
    }
    window.alert(message);
  }, []);

  // Функция handlePaymentCompleted удалена - клиент больше не нажимает "Оплата завершена"
  // Клиент только загружает чек, подтверждение происходит менеджером в Telegram группе

  const handleProceedToPayment = useCallback(async () => {
    const initData = getTelegramInitData();
    if (!initData) {
      const errorMessage = 'Не удалось получить данные Telegram. Откройте mini app из бота.';
      showAlert(errorMessage);
      return;
    }

    // If slots are still loading, avoid "double click" UX:
    // first click triggers validation errors, second click works after context arrives.
    if ((formData.deliveryType === 'delivery' || formData.deliveryType === 'pickup') && deliveryContextLoading) {
      showAlert('Подождите, загружаем доступные дату и время…');
      return;
    }

    // If backend already provided allowed slots, auto-fill date/time once (so user doesn't need to re-select).
    let submitData = formData;
    if ((submitData.deliveryType === 'delivery' || submitData.deliveryType === 'pickup') && deliveryContext?.allowedTimeSlots?.length) {
      const date = submitData.deliveryDate || deliveryContext.allowedTimeSlots[0]?.date || '';
      const slots = deliveryContext.allowedTimeSlots.find((d) => d.date === date)?.slots ?? [];
      const time = submitData.deliveryTime || slots[0] || '';
      if (date && time && (!submitData.deliveryDate || !submitData.deliveryTime)) {
        submitData = { ...submitData, deliveryDate: date, deliveryTime: time };
        setFormData(submitData);
        saveFormData(submitData);
      }
    }

    const isValid = validateForm(submitData);
    if (!isValid) {
      showAlert('Пожалуйста, заполните все обязательные поля');
      return;
    }

    if (orderId) {
      setPaymentStep('payment');
      return;
    }

    setLoading(true);
    setStatusMessage(null);

    try {
      const createdOrder = await createOrder(submitData, items, { todayDate: deliveryContext?.todayDate });

      setOrderId(createdOrder.id);
      setOrderNumber(createdOrder.orderNumber);
      setOrderTotal(createdOrder.total);
      setOrderBonusUsed(createdOrder.bonusUsed ?? 0);
      setPaymentStatus('processing');
      setStatusMessage('Заказ оформлен. Ожидаем подтверждения оплаты.');

      setPaymentStep('payment');
      setPendingOrder(createdOrder.id, createdOrder.orderNumber, createdOrder.total, 'payment');
    } catch (error) {
      console.error('Error creating order:', error);
      showAlert(
        extractApiErrorMessage(error) || 'Произошла ошибка при оформлении заказа. Попробуйте позже.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    validateForm,
    orderId,
    clearCart,
    items,
    showAlert,
    setPendingOrder,
    deliveryContext,
    deliveryContextLoading,
    saveFormData,
  ]);

  const handleApplyPromocode = useCallback(async () => {
    if (!promocodesEnabled) return;
    const code = promocodeInput.trim();
    if (!code) {
      setPromocodeApplied(null);
      setFormData((prev) => (prev ? { ...prev, promocode: '' } : prev));
      if (formData) saveFormData({ ...formData, promocode: '' });
      return;
    }
    setPromocodeApplying(true);
    setPromocodeApplied(null);
    try {
      const subtotal = getTotal();
      const serviceFeeForValidate = Math.round(subtotal * 10) / 100;
      const deliveryCostForValidate =
        formData.deliveryType === 'delivery' && deliveryContext
          ? deliveryContext.deliveryCostRub + deliveryContext.extraCostRub
          : 0;
      const tryOnce = async () => apiValidatePromocode(code, subtotal + serviceFeeForValidate, deliveryCostForValidate);
      let result = await tryOnce();
      // UX hardening: during deploy/network flaps first request can fail; retry once automatically.
      if (!result.success && result.reason && /позже|не удалось|ошибка/i.test(result.reason)) {
        await new Promise((r) => setTimeout(r, 250));
        result = await tryOnce();
      }
      setPromocodeApplied(result);
      if (result.success) {
        const next = { ...formData, promocode: result.code || code };
        setFormData(next);
        saveFormData(next);
      }
    } catch (err) {
      // Retry once on transient network/server errors
      const isAxios = axios.isAxiosError(err);
      const status = isAxios ? err.response?.status : undefined;
      const transient = status == null || status >= 500;
      const subtotal = getTotal();
      const serviceFeeForValidate = Math.round(subtotal * 10) / 100;
      const deliveryCostForValidate =
        formData.deliveryType === 'delivery' && deliveryContext
          ? deliveryContext.deliveryCostRub + deliveryContext.extraCostRub
          : 0;
      if (transient) {
        try {
          await new Promise((r) => setTimeout(r, 250));
          const result = await apiValidatePromocode(code, subtotal + serviceFeeForValidate, deliveryCostForValidate);
          setPromocodeApplied(result);
          if (result.success) {
            const next = { ...formData, promocode: result.code || code };
            setFormData(next);
            saveFormData(next);
          }
        } catch (err2) {
          setPromocodeApplied({
            success: false,
            discountAmount: 0,
            finalPrice: subtotal + serviceFeeForValidate + deliveryCostForValidate,
            reason: extractApiErrorMessage(err2) || 'Не удалось проверить промокод',
          });
        }
      } else {
        setPromocodeApplied({
          success: false,
          discountAmount: 0,
          finalPrice: subtotal + serviceFeeForValidate + deliveryCostForValidate,
          reason: extractApiErrorMessage(err) || 'Не удалось проверить промокод',
        });
      }
    } finally {
      setPromocodeApplying(false);
    }
  }, [promocodeInput, formData, deliveryContext, saveFormData, getTotal, promocodesEnabled]);

  useEffect(() => {
    if (promocodesEnabled) return;
    if (promocodeInput || formData.promocode || promocodeApplied) {
      setPromocodeInput('');
      setPromocodeApplied(null);
      const next = { ...formData, promocode: '' };
      setFormData(next);
      saveFormData(next);
    }
  }, [promocodesEnabled, promocodeInput, promocodeApplied, formData, saveFormData]);

  const handleCopyRequisite = useCallback(
    async (value: string, label: string) => {
      try {
        await navigator.clipboard.writeText(value);
        showAlert(`${label} скопирован`);
      } catch (error) {
        console.error('Failed to copy requisite:', error);
        showAlert(`Не удалось скопировать ${label.toLowerCase()}`);
      }
    },
    [showAlert]
  );

  const handleReceiptChange = useCallback(
    (file?: File | null) => {
      if (!file) {
        return;
      }
      if (!file.type.startsWith('image/')) {
        showAlert('Можно загрузить только изображение.');
        return;
      }
      const maxSize = 4 * 1024 * 1024;
      if (file.size > maxSize) {
        showAlert('Размер изображения не должен превышать 4 МБ.');
        return;
      }
      setReceiptError(null);
      setReceiptSent(false);
      setReceiptFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          setReceiptPreview(result);
        } else {
          showAlert('Не удалось прочитать файл.');
        }
      };
      reader.onerror = () => {
        showAlert('Не удалось прочитать файл.');
      };
      reader.readAsDataURL(file);
    },
    [showAlert]
  );

  const handleUploadReceipt = useCallback(async () => {
    if (!orderId) {
      showAlert('Сначала оформите заказ.');
      return;
    }
    if (!receiptPreview) {
      showAlert('Выберите изображение чека.');
      return;
    }
    setReceiptUploading(true);
    setReceiptError(null);
    try {
      await uploadReceipt(orderId, receiptPreview, receiptFileName);
      setReceiptSent(true);
      setReceiptError(null);
    } catch (error) {
      console.error('Failed to upload receipt:', error);
      // If order is gone (DB reset / expired), unlock checkout.
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        clearPendingOrder();
        setOrderId(null);
        setOrderNumber(null);
        setOrderTotal(null);
        setOrderBonusUsed(0);
        setPaymentStep('form');
        setPaymentStatus('idle');
        setStatusMessage(null);
      }
      const errorMessage = extractApiErrorMessage(error) || 'Не удалось отправить чек. Попробуйте позже.';
      setReceiptError(errorMessage);
      showAlert(errorMessage);
    } finally {
      setReceiptUploading(false);
    }
  }, [orderId, receiptPreview, receiptFileName, clearPendingOrder]);

  const handleCompleteOrder = useCallback(() => {
    if (!receiptSent || !orderId) return;
    clearCart();
    setShowThankYouModal(true);
  }, [receiptSent, orderId, clearCart]);

  const handleCancelOrder = useCallback(async () => {
    if (!orderId) return;
    const confirmed = window.confirm(
      'Отменить заказ? Вы вернётесь к данным заказа, заказ будет отменён.'
    );
    if (!confirmed) return;
    setCancelOrderLoading(true);
    try {
      await cancelOrder(orderId);
      setOrderId(null);
      setOrderNumber(null);
      setOrderTotal(null);
      setOrderBonusUsed(0);
      setPaymentStep('form');
      setPaymentStatus('idle');
      setReceiptSent(false);
      setReceiptPreview(null);
      setReceiptFileName(null);
      setReceiptError(null);
      clearPendingOrder();
    } catch (err) {
      // If order already removed (DB reset), just clear local pending state.
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setOrderId(null);
        setOrderNumber(null);
        setOrderTotal(null);
        setOrderBonusUsed(0);
        setPaymentStep('form');
        setPaymentStatus('idle');
        setReceiptSent(false);
        setReceiptPreview(null);
        setReceiptFileName(null);
        setReceiptError(null);
        setStatusMessage(null);
        clearPendingOrder();
        return;
      }
      const msg = extractApiErrorMessage(err);
      setReceiptError(msg || 'Не удалось отменить заказ');
    } finally {
      setCancelOrderLoading(false);
    }
  }, [orderId, clearPendingOrder]);

  useEffect(() => {
    if (items.length === 0 && !orderId && paymentStep === 'form' && !loading) {
      navigate('/cart', { replace: false });
    }
  }, [items.length, orderId, paymentStep, loading, navigate]);

  useEffect(() => {
    WebApp.MainButton.hide();
  }, []);

  useEffect(() => {
    if (storedOrderId == null) return;
    const EXPIRE_MS = 10 * 60 * 1000;
    // Backward compatibility: older versions stored pendingOrderId without timestamp.
    // Treat such state as stale to avoid a "stuck" payment screen after deploy/DB reset.
    if (storedOrderCreatedAt == null) {
      clearPendingOrder();
      setOrderId(null);
      setOrderNumber(null);
      setOrderTotal(null);
      setPaymentStep('form');
      setPaymentStatus('idle');
      setStatusMessage(null);
      return;
    }
    if (storedOrderCreatedAt != null && Date.now() - storedOrderCreatedAt > EXPIRE_MS) {
      // Auto-unlock: during deploy/network issues checkout can get stuck with a pending order.
      // After 10 minutes we clear the pending state so user can re-place the order.
      clearPendingOrder();
      setOrderId(null);
      setOrderNumber(null);
      setOrderTotal(null);
      setPaymentStep('form');
      setPaymentStatus('idle');
      setStatusMessage(null);
      return;
    }
    setOrderId(storedOrderId);
    if (storedOrderNumber != null) setOrderNumber(storedOrderNumber);
    if (storedOrderTotal != null) setOrderTotal(storedOrderTotal);
    if (storedPaymentStep === 'payment') setPaymentStep('payment');
    setPaymentStatus('processing');
    setStatusMessage('Заказ оформлен. Ожидаем подтверждения оплаты.');
  }, [storedOrderId, storedOrderNumber, storedOrderTotal, storedOrderCreatedAt, storedPaymentStep, clearPendingOrder]);

  useEffect(() => {
    let isActive = true;
    getMe()
      .then((data) => {
        if (!isActive) return;
        setMe(data);
      })
      .catch((error) => {
        console.warn('Failed to load user profile:', error);
      });
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!me || items.length === 0) {
      setBonusEligibility({ maxToUse: 0, hasPromoItems: false });
      return;
    }
    let isActive = true;
    calculateBonusForCart(items.map((i) => ({ productId: i.productId, quantity: i.quantity })))
      .then((data) => {
        if (!isActive) return;
        setBonusEligibility({ maxToUse: data.maxToUse, hasPromoItems: data.hasPromoItems });
      })
      .catch(() => {
        if (!isActive) return;
        setBonusEligibility({ maxToUse: 0, hasPromoItems: false });
      });
    return () => {
      isActive = false;
    };
  }, [me, items]);

  useEffect(() => {
    if (orderId) return;
    const phone = me?.phone || '';
    if (!phone.trim()) return;
    if (formData.phone.trim()) return;
    const updatedData = { ...formData, phone };
    setFormData(updatedData);
    saveFormData(updatedData);
  }, [me?.phone, orderId, formData, saveFormData]);

  // Синхронизируем дату/время с первыми доступными — браузер может показывать первую опцию при value="", но formData остаётся пустым
  useEffect(() => {
    if (orderId || !deliveryContext?.allowedTimeSlots?.length) return;
    const firstDate = earliestDelivery.earliestDate || deliveryContext.todayDate || deliveryContext.allowedTimeSlots[0]?.date || '';
    if (!firstDate) return;
    const allowedDates = deliveryContext.allowedTimeSlots.map((d) => d.date);
    const dateValid = formData.deliveryDate && allowedDates.includes(formData.deliveryDate);
    const effectiveDate = dateValid ? formData.deliveryDate! : firstDate;
    const daySlotsForDate = deliveryContext.allowedTimeSlots.find((d) => d.date === effectiveDate);
    const slotsForDate = daySlotsForDate?.slots ?? [];
    const minTime = slotsForDate[0] ?? getMinDeliveryTime(effectiveDate) ?? earliestDelivery.earliestTime ?? '09:00';
    const timeValid = formData.deliveryTime && slotsForDate.length > 0 && slotsForDate.includes(formData.deliveryTime);
    if (dateValid && timeValid) return;
    const next = {
      ...formData,
      deliveryDate: effectiveDate,
      deliveryTime: timeValid ? formData.deliveryTime! : minTime,
    };
    setFormData(next);
    saveFormData(next);
  }, [orderId, deliveryContext, earliestDelivery.earliestDate, earliestDelivery.earliestTime, getMinDeliveryTime, saveFormData, formData]);

  /** Когда оплата по телефону скрыта, в форме не должно оставаться card_requisites (например из localStorage). */
  useEffect(() => {
    if (formData.paymentType !== 'card_requisites') return;
    const next = { ...formData, paymentType: 'yandex_pay' as const };
    setFormData(next);
    saveFormData(next);
  }, [formData, saveFormData]);

  useEffect(() => {
    if (!customerConfig?.delivery?.city || orderId) {
      return;
    }
    if (formData.address.city && formData.address.city !== 'Чебоксары') {
      return;
    }
    if (formData.address.city === customerConfig.delivery.city) {
      return;
    }
    const updatedData = {
      ...formData,
      address: {
        ...formData.address,
        city: customerConfig.delivery.city,
      },
    };
    setFormData(updatedData);
    saveFormData(updatedData);
  }, [customerConfig?.delivery?.city, orderId, formData, saveFormData]);

  // STEP 2: When applying first saved address, if it has no city use config default; show in UI, user can change.
  useEffect(() => {
    if (orderId || formData.deliveryType !== 'delivery') {
      return;
    }
    if (!savedAddresses.length) {
      return;
    }
    if (formData.address.street || formData.address.house) {
      return;
    }
    const defaultCity = customerConfig?.delivery?.city ?? '';
    const [first] = savedAddresses;
    const city = resolveDeliveryZoneCity(first.city, customerConfig?.delivery?.zones, defaultCity);
    const updatedData = {
      ...formData,
      address: {
        ...formData.address,
        city: city || formData.address.city,
        street: first.street || '',
        house: first.house || '',
        apartment: first.apartment || '',
      },
    };
    setFormData(updatedData);
    saveFormData(updatedData);
  }, [savedAddresses, formData, orderId, saveFormData, customerConfig?.delivery?.city]);


  useEffect(() => {
    if (!orderId || paymentStatus !== 'processing' || receiptSent) {
      return;
    }

    let isActive = true;

    const fetchStatus = async () => {
      try {
        const status = await getOrderStatus(orderId);
        if (!isActive) {
          return;
        }
        if (status.paymentStatus === 'confirmed') {
          setPaymentStatus('confirmed');
          setStatusMessage('Заказ успешно оплачен');
        } else if (status.paymentStatus === 'rejected') {
          setPaymentStatus('rejected');
          setStatusMessage('Оплата не прошла');
        } else {
          setStatusMessage('Платеж обрабатывается');
        }
      } catch (error) {
        // If order no longer exists (DB reset), unlock checkout.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          clearPendingOrder();
          setOrderId(null);
          setOrderNumber(null);
          setOrderTotal(null);
          setOrderBonusUsed(0);
          setPaymentStep('form');
          setPaymentStatus('idle');
          setReceiptSent(false);
          setReceiptPreview(null);
          setReceiptFileName(null);
          setReceiptError(null);
          setStatusMessage(null);
          return;
        }
        console.error('Error fetching order status:', error);
      }
    };

    fetchStatus();
    const intervalId = window.setInterval(fetchStatus, 8000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [orderId, paymentStatus, receiptSent, clearPendingOrder]);

  const handleInputChange = <K extends keyof CheckoutFormData>(field: K, value: CheckoutFormData[K]) => {
    if (orderId) {
      return;
    }
    let updatedData = {
      ...formData,
      [field]: value,
    } as CheckoutFormData;

    // Pickup: only date + approximate time (min time rules same as exact).
    // Reset delivery-only fields so backend slots/validation stay consistent.
    if (field === 'deliveryType') {
      const nextType = value as unknown as 'delivery' | 'pickup';
      if (nextType === 'pickup') {
        updatedData = {
          ...updatedData,
          deliveryTimeType: null,
          deliveryDate: '',
          deliveryTime: '',
        };
      } else {
        // Switching back to delivery: restore default mode if not set.
        updatedData = {
          ...updatedData,
          deliveryTimeType: (updatedData.deliveryTimeType ?? 'approximate') as any,
        };
      }
    }
    setFormData(updatedData);
    // Сохраняем данные при каждом изменении
    saveFormData(updatedData);
    // Очистка ошибки при изменении поля
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddressChange = (field: keyof DeliveryAddress, value: string) => {
    if (orderId) {
      return;
    }
    const updatedData = {
      ...formData,
      address: {
        ...formData.address,
        [field]: value,
      },
    };
    setFormData(updatedData);
    // Сохраняем данные при каждом изменении
    saveFormData(updatedData);
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSelectSavedAddress = (address: {
    city?: string;
    street: string;
    house: string;
    apartment?: string;
  }) => {
    if (orderId) {
      return;
    }
    // STEP 2: If saved address has no city, use config default (e.g. Чебоксары); never submit empty city.
    const defaultCity = customerConfig?.delivery?.city ?? '';
    const city = resolveDeliveryZoneCity(address.city, customerConfig?.delivery?.zones, defaultCity);
    const updatedData = {
      ...formData,
      address: {
        ...formData.address,
        city,
        street: address.street || '',
        house: address.house || '',
        apartment: address.apartment || '',
      },
    };
    setFormData(updatedData);
    saveFormData(updatedData);
  };

  const isOrderLocked = orderId !== null;
  const cartSubtotal = getTotal();
  const serviceFee = Math.round(cartSubtotal * 10) / 100;
  const deliveryCost =
    formData.deliveryType === 'delivery' && deliveryContext
      ? deliveryContext.deliveryCostRub + deliveryContext.extraCostRub
      : 0;
  const effectiveSubtotal = cartSubtotal + serviceFee + (Number.isFinite(deliveryCost) ? deliveryCost : 0);
  const bonusBalance = Number(me?.bonus?.balance || 0);
  const maxBonusToUse = bonusEligibility?.maxToUse ?? 0;
  const bonusesApplied = formData.useBonuses ? maxBonusToUse : 0;
  const hasPromoItems = bonusEligibility?.hasPromoItems ?? false;
  const promocodeDiscount = promocodeApplied?.success && promocodeApplied.discountAmount > 0 ? promocodeApplied.discountAmount : 0;
  const payableTotal = Math.max(0, effectiveSubtotal - bonusesApplied - promocodeDiscount);
  const displayedTotal = orderTotal !== null ? orderTotal : payableTotal;
  const sbpEnabled = Boolean(customerConfig?.sbpQr?.enabled);
  const sbpLabel = sbpEnabled
    ? 'Оплата по QR-коду СБП'
    : `Оплата по QR-коду СБП (${customerConfig?.sbpQr?.note || 'скоро'})`;
  const paymentOptions = [
    { value: 'yandex_pay' as const, label: 'Яндекс Пэй', icon: '💳' as const, disabled: false },
    { value: 'alfa_bank' as const, label: 'Альфа-Банк (по реквизитам)', icon: '🏦' as const, disabled: false },
    { value: 'sbp_qr' as const, label: sbpLabel, icon: '📱' as const, disabled: !sbpEnabled },
  ];
  /** Когда способ оплаты скрыт из списка (например телефон), в форме может остаться старый выбор из localStorage — показываем SBP. */
  const effectivePaymentType = paymentOptions.some((p) => p.value === formData.paymentType)
    ? formData.paymentType
    : 'yandex_pay';
  const selectedPaymentLabel = paymentOptions.find((p) => p.value === effectivePaymentType)?.label ?? sbpLabel;
  /** Ссылка на оплату СБП с суммой в копейках (для NSPK параметр sum) */
  const sbpPaymentUrlWithSum = customerConfig?.sbpQr?.paymentUrl
    ? `${customerConfig.sbpQr.paymentUrl}${customerConfig.sbpQr.paymentUrl.includes('?') ? '&' : '?'}sum=${Math.round(displayedTotal * 100)}`
    : '';
  const yandexPayUrl = customerConfig?.yandexPayUrl || sbpPaymentUrlWithSum;
  const alfaBankPayUrl = customerConfig?.alfaBankUrl || sbpPaymentUrlWithSum;
  const cardRequisitesDetails = customerConfig?.cardRequisites?.details ?? [];
  const requisitesMainLine = cardRequisitesDetails.find((line: string) => {
    const lower = line.toLowerCase();
    return lower.includes('номер телефона') || lower.includes('телефон') || lower.includes('номер карты');
  });
  const requisitesMainLabel = requisitesMainLine?.split(':')[0]?.trim() ?? 'Реквизит';
  const requisitesMainValue = requisitesMainLine?.split(':').slice(1).join(':').trim();
  const requisitesOtherDetails = requisitesMainLine
    ? cardRequisitesDetails.filter((line: string) => line !== requisitesMainLine)
    : cardRequisitesDetails;
  const deliveryZonesText = customerConfig?.delivery?.zones
    ?.map((zone) => `${zone.name} — ${zone.price}`)
    .join('; ');

  if (items.length === 0 && !orderId) {
    return null;
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: 'var(--bg-main)',
      paddingBottom: '120px'
    }}>
      {/* Заголовок с кнопкой назад */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        padding: '12px 16px',
        color: 'var(--text-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <button
          onClick={(e) => {
            e.preventDefault();
            navigate('/cart', { replace: false });
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor"
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <div style={{ fontSize: '16px', fontWeight: 'bold', flex: 1 }}>
          Оформление заказа
        </div>
      </div>

      <div className="container" style={{ paddingTop: '20px' }}>

      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--bg-overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '24px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <p>Оформление заказа...</p>
          </div>
        </div>
      )}

      {paymentStep === 'form' && (
        <>
      {/* Контактная информация */}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid var(--border-light)'
      }}>
        <h2 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Контактная информация
        </h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
            Имя <span style={{ color: 'var(--color-error)' }}>*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Введите ваше имя"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: errors.name ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
              fontSize: '16px',
              boxSizing: 'border-box',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--bg-surface)'
            }}
          />
          {errors.name && (
            <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.name}</p>
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
            Телефон <span style={{ color: 'var(--color-error)' }}>*</span>
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="+7 (999) 123-45-67"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: errors.phone ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
              fontSize: '16px',
              boxSizing: 'border-box',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--bg-surface)'
            }}
          />
          {errors.phone && (
            <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.phone}</p>
          )}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
            Email (необязательно)
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="email@example.com"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: errors.email ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
              fontSize: '16px',
              boxSizing: 'border-box',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--bg-surface)'
            }}
          />
          {errors.email && (
            <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.email}</p>
          )}
        </div>
      </div>

      {/* Тип доставки */}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid var(--border-light)'
      }}>
        <h2 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Способ получения
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            borderRadius: '8px',
            border: formData.deliveryType === 'delivery' ? '2px solid var(--color-accent)' : '1px solid var(--border-soft)',
            cursor: 'pointer',
            backgroundColor: formData.deliveryType === 'delivery' ? 'var(--bg-secondary)' : 'var(--bg-surface)'
          }}>
            <input
              type="radio"
              name="deliveryType"
              value="delivery"
              checked={formData.deliveryType === 'delivery'}
              onChange={() => handleInputChange('deliveryType', 'delivery')}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <div>
              <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>🚚 {deliveryCopy.labelDeliveryTypeDelivery}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Доставим по указанному адресу</div>
            </div>
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            borderRadius: '8px',
            border: formData.deliveryType === 'pickup' ? '2px solid var(--color-accent)' : '1px solid var(--border-soft)',
            cursor: 'pointer',
            backgroundColor: formData.deliveryType === 'pickup' ? 'var(--bg-secondary)' : 'var(--bg-surface)'
          }}>
            <input
              type="radio"
              name="deliveryType"
              value="pickup"
              checked={formData.deliveryType === 'pickup'}
              onChange={() => handleInputChange('deliveryType', 'pickup')}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <div>
              <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>📍 Самовывоз</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Заберёте из нашего магазина</div>
            </div>
          </label>
        </div>

        {/* Адрес доставки */}
        {formData.deliveryType === 'delivery' && (
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: '500', color: 'var(--text-primary)' }}>
              {deliveryCopy.sectionAddress}
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Район / город <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.address.city || ''}
                onChange={(e) => {
                  const city = e.target.value;
                  const next = { ...formData, address: { ...formData.address, city } };
                  setFormData(next);
                  saveFormData(next);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: errors.city ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-surface)',
                }}
              >
                {(customerConfig?.delivery?.zones ?? []).map((zone) => (
                  <option key={zone.name} value={zone.name}>
                    {zone.name} — {zone.price}
                  </option>
                ))}
              </select>
              {errors.city && (
                <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.city}</p>
              )}
            </div>

            {savedAddresses.length > 0 && !orderId && (
              <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Сохранённые адреса</div>
                {savedAddresses.map((addr, index) => {
                  const addressLabel = [addr.city, addr.street, addr.house].filter(Boolean).join(', ');
                  const withApartment = addr.apartment ? `${addressLabel}, кв. ${addr.apartment}` : addressLabel;
                  const isActive =
                    formData.address.street === addr.street &&
                    formData.address.house === addr.house &&
                    (formData.address.apartment || '') === (addr.apartment || '');
                  return (
                    <button
                      key={`${addr.street}-${addr.house}-${index}`}
                      type="button"
                      onClick={() => handleSelectSavedAddress(addr)}
                      style={{
                        textAlign: 'left',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: isActive ? '2px solid var(--color-accent)' : '1px solid var(--border-light)',
                        backgroundColor: isActive ? 'var(--bg-secondary)' : 'var(--bg-surface)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      {withApartment || 'Адрес без названия'}
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                placeholder="Улица"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: errors.street ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
              {errors.street && (
                <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.street}</p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={formData.address.house}
                  onChange={(e) => handleAddressChange('house', e.target.value)}
                  placeholder="Дом"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                  border: errors.house ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--bg-surface)'
                  }}
                />
                {errors.house && (
                  <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.house}</p>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={formData.address.apartment || ''}
                  onChange={(e) => handleAddressChange('apartment', e.target.value)}
                  placeholder="Квартира (необяз.)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-soft)',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--bg-surface)'
                  }}
                />
              </div>
            </div>
            {!orderId && (
              <button
                type="button"
                onClick={() => navigate('/profile?tab=addresses')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Добавить новый адрес
              </button>
            )}
          </div>
        )}
        
        <div style={{ marginTop: '16px', backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '12px', border: '1px solid var(--color-accent-transparent)', color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>{deliveryCopy.sectionDeliveryConditions}</div>
          <div>• {deliveryZonesText || deliveryConditionsZonesFallback}</div>
          <div>
            • {deliveryConditionsWorkingHours(deliveryContext?.display?.workingHours ?? customerConfig?.delivery?.workingHours ?? '09:00–21:00')}
          </div>
          {(deliveryContext?.display?.afterHoursFee ?? customerConfig?.delivery?.afterHoursFee) && (
            <div>
              • {deliveryConditionsAfterHours(
                deliveryContext?.display?.afterHoursStart ?? customerConfig?.delivery?.afterHoursStart ?? '21:00',
                deliveryContext?.display?.afterHoursFee ?? customerConfig?.delivery?.afterHoursFee ?? '500 ₽'
              )}
            </div>
          )}
          <div>• {deliveryConditionsAvgTime(deliveryContext?.display?.avgTime ?? customerConfig?.delivery?.avgTime ?? '1–2 часа')}</div>
        </div>
      </div>

      {/* Дата и время доставки/самовывоза — banner from backend explanationText */}
      {deliveryContext?.explanationText && formData.deliveryType === 'delivery' && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          borderRadius: '12px',
          backgroundColor: deliveryContext.isAfterHours ? 'rgba(220, 53, 69, 0.12)' : 'var(--bg-secondary)',
          border: deliveryContext.isAfterHours ? '1px solid var(--color-error)' : '1px solid var(--border-light)',
          color: deliveryContext.isAfterHours ? 'var(--color-error)' : 'var(--text-secondary)',
          fontSize: '14px',
          fontWeight: 500,
        }}>
          {deliveryContext.explanationText}
        </div>
      )}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid var(--border-light)'
      }}>
        <h2 style={{ fontSize: '18px', marginBottom: '4px', fontWeight: '600', color: 'var(--text-primary)' }}>
          {deliveryCopy.sectionDateAndTime}
        </h2>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>{deliveryCopy.sectionDateAndTimeHint}</p>
        <>
          {deliveryContextLoading && (
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '12px' }}>{loadingSlots}</div>
          )}

          {formData.deliveryType === 'delivery' && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '8px' }}>Время доставки</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '8px',
                  border: (formData.deliveryTimeType ?? 'approximate') === 'exact' ? '2px solid var(--color-accent)' : '1px solid var(--border-soft)',
                  cursor: 'pointer',
                  backgroundColor: (formData.deliveryTimeType ?? 'approximate') === 'exact' ? 'var(--bg-secondary)' : 'var(--bg-surface)',
                }}>
                  <input
                    type="radio"
                    name="deliveryTimeType"
                    checked={(formData.deliveryTimeType ?? 'approximate') === 'exact'}
                    onChange={() => {
                      const next = { ...formData, deliveryTimeType: 'exact' as const };
                      const daySlots = deliveryContext?.allowedTimeSlots?.find((d) => d.date === formData.deliveryDate);
                      const firstSlot = daySlots?.slots?.[0];
                      if (formData.deliveryDate && firstSlot) next.deliveryTime = firstSlot;
                      setFormData(next);
                      saveFormData(next);
                    }}
                    style={{ marginRight: '12px', width: '20px', height: '20px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>Точное время</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Доплата 400 ₽</div>
                  </div>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '8px',
                  border: (formData.deliveryTimeType ?? 'approximate') === 'night' ? '2px solid var(--color-accent)' : '1px solid var(--border-soft)',
                  cursor: 'pointer',
                  backgroundColor: (formData.deliveryTimeType ?? 'approximate') === 'night' ? 'var(--bg-secondary)' : 'var(--bg-surface)',
                }}>
                  <input
                    type="radio"
                    name="deliveryTimeType"
                    checked={(formData.deliveryTimeType ?? 'approximate') === 'night'}
                    onChange={() => {
                      const next = { ...formData, deliveryTimeType: 'night' as const };
                      const daySlots = deliveryContext?.allowedTimeSlots?.find((d) => d.date === formData.deliveryDate);
                      const firstSlot = daySlots?.slots?.[0];
                      if (formData.deliveryDate && firstSlot) next.deliveryTime = firstSlot;
                      setFormData(next);
                      saveFormData(next);
                    }}
                    style={{ marginRight: '12px', width: '20px', height: '20px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>Ночная доставка</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>21:00–09:00 МСК, доплата 500 ₽</div>
                  </div>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '8px',
                  border: (formData.deliveryTimeType ?? 'approximate') === 'approximate' ? '2px solid var(--color-accent)' : '1px solid var(--border-soft)',
                  cursor: 'pointer',
                  backgroundColor: (formData.deliveryTimeType ?? 'approximate') === 'approximate' ? 'var(--bg-secondary)' : 'var(--bg-surface)',
                }}>
                  <input
                    type="radio"
                    name="deliveryTimeType"
                    checked={(formData.deliveryTimeType ?? 'approximate') === 'approximate'}
                    onChange={() => {
                      const next = { ...formData, deliveryTimeType: 'approximate' as const };
                      const daySlots = deliveryContext?.allowedTimeSlots?.find((d) => d.date === formData.deliveryDate);
                      const firstWindow = daySlots?.slots?.[0];
                      if (formData.deliveryDate && firstWindow) next.deliveryTime = firstWindow;
                      setFormData(next);
                      saveFormData(next);
                    }}
                    style={{ marginRight: '12px', width: '20px', height: '20px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>В районе двух часов</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Без доплаты</div>
                  </div>
                </label>
              </div>
              {(formData.deliveryTimeType ?? 'approximate') === 'exact' && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--color-accent-transparent)',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                }}>
                  Доплата за точное время доставки: 400 ₽
                </div>
              )}
              {(formData.deliveryTimeType ?? 'approximate') === 'night' && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--color-accent-transparent)',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                }}>
                  Ночная доставка (21:00–09:00 МСК): доплата 500 ₽
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                {formData.deliveryType === 'pickup' ? 'Дата самовывоза' : deliveryCopy.labelDateRequired} <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.deliveryDate || deliveryContext?.allowedTimeSlots?.[0]?.date || ''}
                onChange={(e) => {
                  const date = e.target.value;
                  const minT = getMinDeliveryTime(date);
                  let time = formData.deliveryTime || minT || '12:00';
                  if (date && minT && time < minT) time = minT;
                  const next = { ...formData, deliveryDate: date, deliveryTime: time };
                  setFormData(next);
                  saveFormData(next);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: errors.deliveryDate ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-surface)'
                }}
              >
                {(() => {
                  if (!deliveryContext?.allowedTimeSlots?.length) {
                    return <option value="">{noSlotsPlaceholder}</option>;
                  }
                  const todayDate = deliveryContext.todayDate ?? '';
                  const tomorrowDate = deliveryContext.tomorrowDate ?? '';
                  const monthShort = (mm: string) => {
                    const m = parseInt(mm, 10) || 1;
                    const names = ['янв.', 'февр.', 'март', 'апр.', 'мая', 'июня', 'июля', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'];
                    return names[m - 1] ?? mm;
                  };
                  return deliveryContext.allowedTimeSlots.map(({ date: value }) => {
                    const day = value.slice(8, 10);
                    const month = value.slice(5, 7);
                    const label = value === todayDate ? `Сегодня, ${parseInt(day, 10)} ${monthShort(month)}` : value === tomorrowDate ? `Завтра, ${parseInt(day, 10)} ${monthShort(month)}` : `${parseInt(day, 10)} ${monthShort(month)}`;
                    return <option key={value} value={value}>{label}</option>;
                  });
                })()}
              </select>
              {errors.deliveryDate && (
                <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.deliveryDate}</p>
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                {formData.deliveryType === 'pickup' ? 'Время самовывоза' : deliveryCopy.labelTimeRequired} <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.deliveryTime || (() => {
                  const dateForSlots = formData.deliveryDate || deliveryContext?.allowedTimeSlots?.[0]?.date || '';
                  const daySlots = deliveryContext?.allowedTimeSlots?.find((d) => d.date === dateForSlots);
                  return daySlots?.slots?.[0] || '';
                })()}
                onChange={(e) => handleInputChange('deliveryTime', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: errors.deliveryTime ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-surface)'
                }}
              >
                {(() => {
                  const dateForSlots = formData.deliveryDate || deliveryContext?.allowedTimeSlots?.[0]?.date || '';
                  const daySlots = deliveryContext?.allowedTimeSlots?.find((d) => d.date === dateForSlots);
                  const slots = daySlots?.slots ?? [];
                  return slots.length ? slots.map((s) => <option key={s} value={s}>{s}</option>) : <option value="">{selectDateFirst}</option>;
                })()}
              </select>
              {errors.deliveryTime && (
                <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.deliveryTime}</p>
              )}
            </div>
          </div>
        </>
      </div>

      {/* Получатель */}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid var(--border-light)'
      }}>
        <h2 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Данные получателя
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
              Имя получателя <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.recipientName || ''}
              onChange={(e) => handleInputChange('recipientName', e.target.value)}
              placeholder="Кому доставить букет"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: errors.recipientName ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
                fontSize: '16px',
                boxSizing: 'border-box',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-surface)'
              }}
            />
            {errors.recipientName && (
              <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.recipientName}</p>
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
              Телефон получателя <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="tel"
              value={formData.recipientPhone || ''}
              onChange={(e) => handleInputChange('recipientPhone', e.target.value)}
              placeholder="+7 (999) 123-45-67"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: errors.recipientPhone ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
                fontSize: '16px',
                boxSizing: 'border-box',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-surface)'
              }}
            />
            {errors.recipientPhone && (
              <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.recipientPhone}</p>
            )}
          </div>
        </div>
      </div>

      {/* Открытка */}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid var(--border-light)'
      }}>
        <h2 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Текст открытки
        </h2>
        <textarea
          value={formData.cardText || ''}
          onChange={(e) => handleInputChange('cardText', e.target.value)}
          placeholder="Что написать в открытке"
          rows={3}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
          border: errors.cardText ? '2px solid var(--color-error)' : '1px solid var(--border-soft)',
            fontSize: '16px',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
          color: 'var(--text-primary)',
          backgroundColor: 'var(--bg-surface)'
          }}
        />
        {errors.cardText && (
          <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>{errors.cardText}</p>
        )}
      </div>

      {/* Комментарий */}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid var(--border-light)'
      }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
          Комментарий к заказу
        </label>
        <textarea
          value={formData.comment}
          onChange={(e) => handleInputChange('comment', e.target.value)}
          placeholder="Дополнительная информация для курьера..."
          rows={4}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
          border: '1px solid var(--border-soft)',
            fontSize: '16px',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
          color: 'var(--text-primary)',
          backgroundColor: 'var(--bg-surface)'
          }}
        />
      </div>

      {/* Промокод (платная функция, зависит от ADMIN_DASH) */}
      {promocodesEnabled && (
        <div
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            border: '1px solid var(--border-light)',
          }}
        >
          <h2 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Промокод
          </h2>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              type="text"
              placeholder="Введите промокод"
              value={promocodeInput}
              onChange={(e) => setPromocodeInput(e.target.value.toUpperCase())}
              disabled={isOrderLocked}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-main)',
              }}
            />
            <button
              type="button"
              onClick={handleApplyPromocode}
              disabled={promocodeApplying || isOrderLocked}
              title="Применить промокод"
              style={{
                width: '44px',
                minWidth: '44px',
                padding: 0,
                borderRadius: '8px',
                border: 'none',
                backgroundColor: promocodeApplying ? 'var(--bg-disabled)' : 'var(--color-accent)',
                color: 'var(--text-on-accent)',
                fontSize: '20px',
                lineHeight: 1,
                cursor: promocodeApplying || isOrderLocked ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {promocodeApplying ? '…' : '✓'}
            </button>
          </div>
          {promocodeApplied?.success && promocodeApplied.discountAmount > 0 && (
            <div style={{ fontSize: '14px', color: 'var(--color-accent)' }}>
              Промокод применён: скидка −{promocodeApplied.discountAmount.toLocaleString('ru-RU')} ₽
            </div>
          )}
          {promocodeApplied && !promocodeApplied.success && promocodeApplied.reason && (
            <div style={{ fontSize: '13px', color: 'var(--text-error, #c0392b)' }}>
              {promocodeApplied.reason}
            </div>
          )}
        </div>
      )}

      {/* Бонусы */}
      {me && (
        <div
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            border: '1px solid var(--border-light)',
          }}
        >
          <h2 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Бонусы
          </h2>
          <div style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Баланс: <strong>{bonusBalance.toLocaleString('ru-RU')} ₽</strong>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Кэшбек: <strong>{me.bonus.cashbackPercent}%</strong> · Списание до <strong>{me.bonus.maxSpendPercent}%</strong>
          </div>

          {maxBonusToUse > 0 ? (
            <>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'center', cursor: isOrderLocked ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(formData.useBonuses)}
                  disabled={isOrderLocked}
                  onChange={(e) => handleInputChange('useBonuses', e.target.checked)}
                />
                <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                  Использовать бонусы (до {maxBonusToUse.toLocaleString('ru-RU')} ₽)
                </span>
              </label>
              {hasPromoItems && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Бонусы не применяются к товарам по акции
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {hasPromoItems
                ? 'Бонусы нельзя использовать: в корзине только товары по акции'
                : 'Бонусов для списания пока нет.'}
            </div>
          )}

          {Boolean(formData.useBonuses) && maxBonusToUse > 0 && (
            <div style={{ marginTop: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Списываем: <strong>{maxBonusToUse.toLocaleString('ru-RU')} ₽</strong>
              <br />
              <span style={{ fontSize: '12px' }}>При списании бонусов кэшбэк не начисляется</span>
            </div>
          )}
        </div>
      )}

      {/* Итого */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-secondary)' }}>
            <span>Товары</span>
            <span>{cartSubtotal.toLocaleString('ru-RU')} ₽</span>
          </div>
          {serviceFee > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-secondary)' }}>
              <span>Сервисный сбор 10%</span>
              <span>{serviceFee.toLocaleString('ru-RU')} ₽</span>
            </div>
          )}
          {deliveryCost > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-secondary)' }}>
              <span>{deliveryCopy.labelDeliveryCost}</span>
              <span>{deliveryCost.toLocaleString('ru-RU')} ₽</span>
            </div>
          )}
          {bonusesApplied > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-secondary)' }}>
              <span>Списано бонусами</span>
              <span>−{bonusesApplied.toLocaleString('ru-RU')} ₽</span>
            </div>
          )}
          {promocodesEnabled && promocodeDiscount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-secondary)' }}>
              <span>Промокод{promocodeApplied?.code ? ` (${promocodeApplied.code})` : ''}</span>
              <span>−{promocodeDiscount.toLocaleString('ru-RU')} ₽</span>
            </div>
          )}
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>Итого:</span>
          <span style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: 'var(--color-accent)'
          }}>
            {displayedTotal.toLocaleString('ru-RU')} ₽
          </span>
        </div>
      </div>
      <button
        onClick={handleProceedToPayment}
        disabled={loading}
        style={{
          width: '100%',
          padding: '14px',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: loading ? 'var(--bg-disabled)' : 'var(--color-accent)',
          color: 'var(--text-on-accent)',
          fontSize: '16px',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer'
        }}
      >
        Оформить заказ
      </button>
      </>
      )}

      {paymentStep === 'payment' && (
        <>
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={() => setPaymentStep('form')}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--color-accent)',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              ← Вернуться к данным заказа
            </button>
          </div>

          {/* Сумма и бонусы в начале шага оплаты */}
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            border: '1px solid var(--border-light)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: orderBonusUsed > 0 || (orderTotal != null && orderTotal > 0) ? '12px' : 0
            }}>
              <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>Сумма к оплате:</span>
              <span style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-accent)' }}>
                {(orderTotal != null ? orderTotal : displayedTotal).toLocaleString('ru-RU')} ₽
              </span>
            </div>
            {orderBonusUsed > 0 ? (
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Списано бонусами: <strong style={{ color: 'var(--text-primary)' }}>{orderBonusUsed.toLocaleString('ru-RU')} ₽</strong>
              </div>
            ) : (
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                После подтверждения заказа начислится: <strong style={{ color: 'var(--text-primary)' }}>
                  ~{Math.max(0, Math.floor((Number(orderTotal ?? displayedTotal) * Number(me?.bonus?.cashbackPercent ?? 0)) / 100))} ₽
                </strong> бонусами (кэшбек {me?.bonus?.cashbackPercent ?? 0}%)
              </div>
            )}
          </div>

          {/* Способ оплаты */}
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            border: '1px solid var(--border-light)'
          }}>
            <h2 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Способ оплаты
            </h2>

            {!isOrderLocked && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {paymentOptions.map((payment) => (
                  <label
                    key={payment.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      borderRadius: '8px',
                      border: effectivePaymentType === payment.value ? '2px solid var(--color-accent)' : '1px solid var(--border-soft)',
                      cursor: payment.disabled ? 'not-allowed' : 'pointer',
                      opacity: payment.disabled ? 0.6 : 1,
                      backgroundColor: effectivePaymentType === payment.value ? 'var(--bg-secondary)' : 'var(--bg-surface)'
                    }}
                  >
                    <input
                      type="radio"
                      name="paymentType"
                      value={payment.value}
                      checked={effectivePaymentType === payment.value}
                      disabled={payment.disabled || isOrderLocked}
                      onChange={() => handleInputChange('paymentType', payment.value)}
                      style={{ marginRight: '12px', width: '20px', height: '20px' }}
                    />
                    <span style={{ marginRight: '8px', fontSize: '20px' }}>{payment.icon}</span>
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{payment.label}</span>
                  </label>
                ))}
              </div>
            )}
            {isOrderLocked && (
              <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                Выбранный способ оплаты: <strong>{selectedPaymentLabel || '—'}</strong>
              </div>
            )}

            {effectivePaymentType === 'yandex_pay' && (
              <div style={{
                marginTop: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid var(--color-accent-transparent)'
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Оплата через Яндекс Пэй
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                  Оплатите заказ по ссылке ниже, затем загрузите чек об оплате.
                </div>
                {yandexPayUrl && (
                  <a
                    href={yandexPayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      marginTop: '12px',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--text-on-accent)',
                      fontSize: '15px',
                      fontWeight: 600,
                      textDecoration: 'none',
                      textAlign: 'center'
                    }}
                  >
                    Перейти к оплате
                  </a>
                )}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Загрузите чек или скриншот оплаты
                  </div>
                  <label style={{ display: 'inline-block', marginBottom: '8px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleReceiptChange(e.target.files?.[0])}
                      disabled={receiptUploading}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      display: 'inline-block',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      cursor: receiptUploading ? 'not-allowed' : 'pointer'
                    }}>
                      Выбрать файл
                    </span>
                  </label>
                  {receiptPreview && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <img
                        src={receiptPreview}
                        alt="Чек"
                        style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                      />
                      {receiptFileName && (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {receiptFileName}
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleUploadReceipt}
                    disabled={receiptUploading || !receiptPreview}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: receiptUploading || !receiptPreview ? 'var(--bg-disabled)' : 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: receiptUploading || !receiptPreview ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {receiptUploading ? 'Отправляем чек...' : 'Отправить чек'}
                  </button>
                  {receiptSent && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Чек отправлен менеджеру.
                    </div>
                  )}
                  {receiptError && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-error)' }}>
                      {receiptError}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleCompleteOrder}
                    disabled={!receiptSent}
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      padding: '14px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: receiptSent ? 'var(--color-accent)' : 'var(--bg-disabled)',
                      color: receiptSent ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: receiptSent ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Завершить заказ
                  </button>
                </div>
              </div>
            )}

            {effectivePaymentType === 'alfa_bank' && (
              <div style={{
                marginTop: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid var(--color-accent-transparent)'
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Оплата через Альфа-Банк
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                  Оплатите заказ по ссылке ниже, затем загрузите чек об оплате.
                </div>
                {alfaBankPayUrl && (
                  <a
                    href={alfaBankPayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      marginTop: '12px',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--text-on-accent)',
                      fontSize: '15px',
                      fontWeight: 600,
                      textDecoration: 'none',
                      textAlign: 'center'
                    }}
                  >
                    Перейти к оплате
                  </a>
                )}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Загрузите чек или скриншот оплаты
                  </div>
                  <label style={{ display: 'inline-block', marginBottom: '8px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleReceiptChange(e.target.files?.[0])}
                      disabled={receiptUploading}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      display: 'inline-block',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      cursor: receiptUploading ? 'not-allowed' : 'pointer'
                    }}>
                      Выбрать файл
                    </span>
                  </label>
                  {receiptPreview && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <img
                        src={receiptPreview}
                        alt="Чек"
                        style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                      />
                      {receiptFileName && (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {receiptFileName}
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleUploadReceipt}
                    disabled={receiptUploading || !receiptPreview}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: receiptUploading || !receiptPreview ? 'var(--bg-disabled)' : 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: receiptUploading || !receiptPreview ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {receiptUploading ? 'Отправляем чек...' : 'Отправить чек'}
                  </button>
                  {receiptSent && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Чек отправлен менеджеру.
                    </div>
                  )}
                  {receiptError && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-error)' }}>
                      {receiptError}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleCompleteOrder}
                    disabled={!receiptSent}
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      padding: '14px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: receiptSent ? 'var(--color-accent)' : 'var(--bg-disabled)',
                      color: receiptSent ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: receiptSent ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Завершить заказ
                  </button>
                </div>
              </div>
            )}

            {effectivePaymentType === 'card_requisites' && (
              <div style={{
                marginTop: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid var(--color-accent-transparent)'
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  {customerConfig?.cardRequisites.title || 'Оплата по номеру телефона'}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                  {requisitesMainValue && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        {requisitesMainLabel}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => handleCopyRequisite(requisitesMainValue, requisitesMainLabel)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            fontSize: '16px',
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            cursor: 'pointer'
                          }}
                        >
                          {requisitesMainValue}
                        </button>
                        <button
                          onClick={() => handleCopyRequisite(requisitesMainValue, requisitesMainLabel)}
                          aria-label={`Скопировать ${requisitesMainLabel.toLowerCase()}`}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            border: '1px solid var(--border-soft)',
                            backgroundColor: 'var(--bg-surface)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  )}
                  {requisitesOtherDetails.length
                    ? requisitesOtherDetails.map((line) => (
                        <div key={line}>{line}</div>
                      ))
                    : !requisitesMainValue && <div>Реквизиты будут добавлены позже.</div>}
                </div>
                <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {customerConfig?.cardRequisites.note || 'После оплаты пришлите чек, пожалуйста.'}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Загрузите чек или скриншот оплаты
                  </div>
                  <label style={{ display: 'inline-block', marginBottom: '8px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleReceiptChange(e.target.files?.[0])}
                      disabled={receiptUploading}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      display: 'inline-block',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      cursor: receiptUploading ? 'not-allowed' : 'pointer'
                    }}>
                      Выбрать файл
                    </span>
                  </label>
                  {receiptPreview && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <img
                        src={receiptPreview}
                        alt="Чек"
                        style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                      />
                      {receiptFileName && (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {receiptFileName}
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleUploadReceipt}
                    disabled={receiptUploading || !receiptPreview}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: receiptUploading || !receiptPreview ? 'var(--bg-disabled)' : 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: receiptUploading || !receiptPreview ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {receiptUploading ? 'Отправляем чек...' : 'Отправить чек'}
                  </button>
                  {receiptSent && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Чек отправлен менеджеру.
                    </div>
                  )}
                  {receiptError && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-error)' }}>
                      {receiptError}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleCompleteOrder}
                    disabled={!receiptSent}
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      padding: '14px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: receiptSent ? 'var(--color-accent)' : 'var(--bg-disabled)',
                      color: receiptSent ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: receiptSent ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Завершить заказ
                  </button>
                </div>
              </div>
            )}

            {effectivePaymentType === 'sbp_qr' && (
              <div style={{
                marginTop: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid var(--color-accent-transparent)'
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Оплата по QR-коду СБП
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                  {customerConfig?.sbpQr?.note || 'Отсканируйте QR и оплатите, затем загрузите чек об оплате.'}
                </div>
                {sbpPaymentUrlWithSum && (
                  <a
                    href={sbpPaymentUrlWithSum}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      marginTop: '12px',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--text-on-accent)',
                      fontSize: '15px',
                      fontWeight: 600,
                      textDecoration: 'none',
                      textAlign: 'center'
                    }}
                  >
                    Перейти к оплате
                  </a>
                )}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Загрузите чек или скриншот оплаты
                  </div>
                  <label style={{ display: 'inline-block', marginBottom: '8px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleReceiptChange(e.target.files?.[0])}
                      disabled={receiptUploading}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      display: 'inline-block',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      cursor: receiptUploading ? 'not-allowed' : 'pointer'
                    }}>
                      Выбрать файл
                    </span>
                  </label>
                  {receiptPreview && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <img
                        src={receiptPreview}
                        alt="Чек"
                        style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                      />
                      {receiptFileName && (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {receiptFileName}
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleUploadReceipt}
                    disabled={receiptUploading || !receiptPreview}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      backgroundColor: receiptUploading || !receiptPreview ? 'var(--bg-disabled)' : 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: receiptUploading || !receiptPreview ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {receiptUploading ? 'Отправляем чек...' : 'Отправить чек'}
                  </button>
                  {receiptSent && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Чек отправлен менеджеру.
                    </div>
                  )}
                  {receiptError && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-error)' }}>
                      {receiptError}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleCompleteOrder}
                    disabled={!receiptSent}
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      padding: '14px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: receiptSent ? 'var(--color-accent)' : 'var(--bg-disabled)',
                      color: receiptSent ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: receiptSent ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Завершить заказ
                  </button>
                </div>
              </div>
            )}

            {orderId && (
              <div style={{
                marginTop: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>
                  {orderNumber ? `Заказ #${orderNumber}` : 'Заказ отправлен'}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                  {statusMessage || 'Платеж обрабатывается'}
                </div>
                {paymentStatus === 'rejected' && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Открыта тема в поддержке. Напишите «Оплата не прошла» в чат бота — менеджер ответит там же.
                  </div>
                )}
              </div>
            )}

            {/* Отменить заказ — внизу шага оплаты */}
            {orderId && (
              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border-light)' }}>
                <button
                  type="button"
                  onClick={handleCancelOrder}
                  disabled={cancelOrderLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-soft)',
                    backgroundColor: 'transparent',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: cancelOrderLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {cancelOrderLoading ? 'Отмена заказа...' : 'Отменить заказ'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
      </div>
      <AppFooter />
      <BottomNavigation />

      {/* Модальное окно: заказ успешно завершён */}
      {showThankYouModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(65, 65, 67, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderRadius: '24px',
              padding: '32px 24px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(215, 149, 176, 0.3)',
              position: 'relative',
              animation: 'fadeIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              transform: 'scale(1)',
              border: '1px solid rgba(215, 149, 176, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(215, 149, 176, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '48px',
                lineHeight: '1'
              }}
            >
              🌺
            </div>

            <h2
              style={{
                fontSize: '24px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                textAlign: 'center',
                margin: '0 0 12px',
                letterSpacing: '-0.02em'
              }}
            >
              Заказ успешно завершён
            </h2>

            <p
              style={{
                fontSize: '15px',
                color: 'var(--text-secondary)',
                textAlign: 'center',
                lineHeight: '1.6',
                margin: '0 0 32px',
                padding: '0 8px'
              }}
            >
              После подтверждения оплаты вам придёт уведомление. Статус заказа можно посмотреть в разделе «Мои заказы» в профиле.
            </p>

            <button
              onClick={() => {
                clearPendingOrder();
                setShowThankYouModal(false);
                navigate('/catalog');
              }}
              style={{
                width: '100%',
                padding: '14px 24px',
                borderRadius: '16px',
                border: 'none',
                backgroundColor: 'var(--color-accent)',
                color: 'var(--text-on-accent)',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(215, 149, 176, 0.3)',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.01em'
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.97)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(215, 149, 176, 0.25)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(215, 149, 176, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(215, 149, 176, 0.3)';
              }}
            >
              Перейти в каталог
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
